/* ============================================================
   The Rare Steps — Editorial collection (PLP) behaviours
   - "Load more" pagination that appends products in place
   - Sticky occasion pill bar shadow on scroll
   Works alongside Dawn's facets.js (which re-renders the grid
   container on filter/sort changes); all handlers are bound via
   event delegation so they survive those re-renders.
   ============================================================ */
(function () {
  function _debounceFn(fn, wait) {
    var t;
    return function() {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(ctx, args); }, wait);
    };
  }

  if (window.__trsCollectionInit) return;
  window.__trsCollectionInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Load more --------------------------------------------------- */
  function updateProgress(container) {
    const grid = container.querySelector('#product-grid');
    const wrap = container.querySelector('[data-trs-loadmore]');
    if (!grid || !wrap) return;
    const shown = grid.querySelectorAll('.grid__item:not(.grid__item--spotlight)').length;
    const total = parseInt(wrap.getAttribute('data-total') || '0', 10);
    const status = wrap.querySelector('[data-trs-loadmore-status]');
    const bar = wrap.querySelector('[data-trs-loadmore-bar] span');
    // Only write when values change — textContent mutations inside
    // ProductGridContainer would otherwise re-fire the facets MutationObserver.
    if (status && total) {
      const next = 'Showing ' + shown + ' of ' + total;
      if (status.textContent !== next) status.textContent = next;
    }
    if (bar && total) {
      const width = Math.min(100, Math.round((shown / total) * 100)) + '%';
      if (bar.style.width !== width) bar.style.width = width;
    }
  }

  async function loadMore(btn) {
    const url = btn.getAttribute('href');
    if (!url || btn.getAttribute('aria-busy') === 'true') return;

    const container = document.getElementById('ProductGridContainer');
    const grid = container && container.querySelector('#product-grid');
    if (!grid) return;

    btn.setAttribute('aria-busy', 'true');
    const originalLabel = btn.textContent;
    btn.textContent = 'Loading…';

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Network error');
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newItems = doc.querySelectorAll('#product-grid > .grid__item:not(.grid__item--spotlight)');
      const frag = document.createDocumentFragment();
      newItems.forEach((item) => {
        // Make appended cards visible immediately (Dawn's scroll observer
        // only watches nodes present at load).
        item.classList.remove('scroll-trigger', 'animate--slide-in');
        item.removeAttribute('data-cascade');
        if (reduceMotion) {
          frag.appendChild(item);
        } else {
          item.style.opacity = '0';
          item.style.transform = 'translateY(1.2rem)';
          item.style.transition = 'opacity .5s ease, transform .5s ease';
          frag.appendChild(item);
        }
      });
      grid.appendChild(frag);

      if (!reduceMotion) {
        requestAnimationFrame(() => {
          newItems.forEach((item) => {
            item.style.opacity = '';
            item.style.transform = '';
          });
        });
      }

      // Advance the button to the next page, or retire it.
      const nextBtn = doc.querySelector('[data-trs-loadmore-btn]');
      if (nextBtn && nextBtn.getAttribute('href')) {
        btn.setAttribute('href', nextBtn.getAttribute('href'));
        btn.textContent = originalLabel;
        btn.removeAttribute('aria-busy');
      } else {
        const wrap = btn.closest('[data-trs-loadmore]');
        btn.remove();
        if (wrap) {
          const done = wrap.querySelector('[data-trs-loadmore-status]');
          if (done) done.textContent = "You've reached the end of this edit";
        }
      }
      updateProgress(container);
    } catch (e) {
      btn.textContent = originalLabel;
      btn.removeAttribute('aria-busy');
      // Fall back to a normal navigation if the fetch fails.
      window.location.href = url;
    }
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-trs-loadmore-btn]');
    if (!btn) return;
    e.preventDefault();
    loadMore(btn);
  });

  /* ---------- Collection occasion pills (ensure navigation) ---------- */
  function getCollectionPath() {
    return window.location.pathname.split('?')[0];
  }

  function getOccasionFromUrl() {
    return (new URLSearchParams(window.location.search).get('occasion') || '').toLowerCase();
  }

  function decodePillHref(rawHref) {
    if (!rawHref || rawHref === '#') return '';
    try {
      return decodeURIComponent(rawHref);
    } catch (err) {
      return rawHref;
    }
  }

  function resolvePillHref(pill) {
    const collectionPath = getCollectionPath();
    const occasion = (pill.getAttribute('data-occasion') || '').trim();

    if (occasion) {
      return collectionPath + '?occasion=' + encodeURIComponent(occasion);
    }

    let href = decodePillHref(pill.getAttribute('href') || '');
    if (href.startsWith('http')) {
      try {
        const url = new URL(href);
        return url.pathname;
      } catch (err) {
        return collectionPath;
      }
    }

    if (href.startsWith('/')) {
      return href.split('?')[0];
    }

    return collectionPath;
  }

  function normalizePillHrefs() {
    document.querySelectorAll('.trs-cl-occ-filters a[data-trs-occasion-pill][href]').forEach(function (pill) {
      pill.setAttribute('href', resolvePillHref(pill));
    });
  }

  function syncOccasionPills(occasion) {
    const pills = document.querySelectorAll('.trs-cl-occ-filters a[data-trs-occasion-pill][href]');
    if (!pills.length) return;

    pills.forEach(function (pill) {
      const pillOccasion = (pill.getAttribute('data-occasion') || '').toLowerCase();
      const isAll = !pillOccasion;
      const isActive = occasion ? pillOccasion === occasion : isAll;
      pill.classList.toggle('trs-occ-filters__pill--active', isActive);
      if (isActive) {
        pill.setAttribute('aria-current', 'true');
      } else {
        pill.removeAttribute('aria-current');
      }
    });
  }

  // Prevents MutationObserver ↔ applyOccasionFilter feedback when load-more
  // status text (inside ProductGridContainer) is updated.
  let occasionFilterBusy = false;

  function applyOccasionFilter() {
    if (occasionFilterBusy) return;
    occasionFilterBusy = true;
    try {
      normalizePillHrefs();

      const occasion = getOccasionFromUrl();
      const grid = document.querySelector('#product-grid');
      if (!grid) return;

      let visible = 0;
      grid.querySelectorAll(':scope > .grid__item:not(.grid__item--spotlight)').forEach(function (item) {
        const raw = item.getAttribute('data-trs-occasions') || '';
        const categories = raw
          .toLowerCase()
          .split('|')
          .map(function (part) {
            return part.trim();
          })
          .filter(Boolean);
        const show = !occasion || categories.indexOf(occasion) !== -1;
        item.hidden = !show;
        if (show) visible += 1;
      });

      syncOccasionPills(occasion);

      const emptyState = document.querySelector('[data-trs-occasion-empty]');
      if (emptyState) {
        emptyState.hidden = !occasion || visible > 0;
      }
      grid.hidden = Boolean(occasion && visible === 0);

      const countNodes = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
      countNodes.forEach(function (node) {
        if (!node || !occasion) return;
        const next = visible + ' product' + (visible === 1 ? '' : 's');
        if (node.textContent !== next) node.textContent = next;
      });

      const container = document.getElementById('ProductGridContainer');
      if (container) updateProgress(container);
    } finally {
      occasionFilterBusy = false;
    }
  }

  document.addEventListener(
    'click',
    function (e) {
      const pill = e.target.closest('.trs-cl-occ-filters a[data-trs-occasion-pill][href]');
      if (!pill) return;
      const href = resolvePillHref(pill);
      if (!href || href === '#') return;
      const target = new URL(href, window.location.origin);
      const current = window.location.pathname + window.location.search;
      const next = target.pathname + target.search;
      if (current === next) return;
      e.preventDefault();
      window.location.assign(href);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', applyOccasionFilter);
  document.addEventListener('shopify:section:load', applyOccasionFilter);

  /* Observe facets.js grid swaps so occasion filter persists after sort/filter.
     Also re-applies occasion filtering to cards appended by Load more.
     Disconnect while applying so our own DOM writes cannot re-enter. */
  function observeProductGrid() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || !('MutationObserver' in window)) return;
    const observer = new MutationObserver(function () {
      observer.disconnect();
      try {
        applyOccasionFilter();
      } finally {
        observer.observe(container, { childList: true, subtree: true });
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') {
    observeProductGrid();
  } else {
    document.addEventListener('DOMContentLoaded', observeProductGrid);
  }

  /* ---------- Sticky pill bar shadow -------------------------------------- */
  function initStickyPills() {
    const bar = document.querySelector('.occasion-pills.trs-cl-sticky');
    if (!bar) return;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;height:1px;width:1px;';
    bar.parentNode.insertBefore(sentinel, bar);

    let io;
    const observeStuck = () => {
      const headerHeight =
        getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() || '0px';
      if (io) io.disconnect();
      io = new IntersectionObserver(
        (entries) => {
          bar.classList.toggle('is-stuck', !entries[0].isIntersecting);
        },
        { threshold: 0, rootMargin: `-${headerHeight} 0px 0px 0px` }
      );
      io.observe(sentinel);
    };

    observeStuck();
    window.addEventListener('resize', _debounceFn(observeStuck, 200));
    document.addEventListener('shopify:section:load', observeStuck);
  }

  if (document.readyState !== 'loading') {
    initStickyPills();
  } else {
    document.addEventListener('DOMContentLoaded', initStickyPills);
  }

  // Re-init progress on first paint.
  document.addEventListener('DOMContentLoaded', function () {
    const container = document.getElementById('ProductGridContainer');
    if (container) updateProgress(container);
  });
})();
